import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { listIssues, type DiscussionSubject, type Issue } from "../../api/content/discussionsApi";
import ProjectNeeded from "../../projects/ProjectNeeded";
import { useProjects } from "../../projects/projectContext";
import { useLoad } from "../../shared/loading/useLoad";
import EmptyState from "../../ui/feedback/EmptyState";
import LoadStatus from "../../ui/feedback/LoadStatus";
import DiscussionPanel from "./DiscussionPanel";

/**
 * `/issues`: the chosen project's issues (FR-VERSION-6), each subject's discussion in the one
 * `DiscussionPanel`. On the installation's DMW project these are the problem reports (F13,
 * W-08b), which only account administrators may read (ADR-0037 D5).
 */
export default function ProjectIssuesPage() {
  const { t } = useTranslation();
  const projectId = useProjects().selectedProjectId;
  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <h2 className="app-title text-xl font-semibold">{t("discussion.issuesTitle")}</h2>
      {projectId === null ? <ProjectNeeded /> : <ProjectIssues key={projectId} projectId={projectId} />}
    </div>
  );
}

function ProjectIssues({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listIssues(projectId, signal), [projectId]);
  const { state, reload, refresh } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  if (state.value.length === 0) return <EmptyState>{t("discussion.noIssues")}</EmptyState>;
  return (
    <div className="space-y-6">
      {subjectsOf(state.value).map((subject) => (
        // A change refreshes in place, so a reply still being written in another panel stays.
        <DiscussionPanel key={`${subject.kind}:${subject.value}`} projectId={projectId} subject={subject} onChanged={refresh} />
      ))}
    </div>
  );
}

/** Each subject the issues are on, once, newest issue's subject first. */
function subjectsOf(issues: readonly Issue[]): readonly DiscussionSubject[] {
  const seen = new Map<string, DiscussionSubject>();
  for (const issue of [...issues].reverse()) {
    const key = `${issue.subject.kind}:${issue.subject.value}`;
    if (!seen.has(key)) seen.set(key, issue.subject);
  }
  return [...seen.values()];
}
